'use client'

import React from 'react'
import { cn, openInNewTab } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

// ---------------------------------------------------------------------------
// RedirectionBlock
//
// Rendered below an AI message bubble for every internal link found in the
// message content. Styled to exactly match BriefingDownloadCard /
// NavigationCard — a branded horizontal row with icon bubble, label +
// subtitle, and external-link arrow.
//
// Unlike EntityChip (the tiny inline pill), this is a full-width block that
// appears at the bottom of the message for maximum discoverability.
// ---------------------------------------------------------------------------

// Entity type metadata — mirrors EntityChip.tsx and NavigationCard.tsx
type EntityType =
  | 'claim'
  | 'sample'
  | 'rfi'
  | 'da_worksheet'
  | 'rebuttal'
  | 'dispute'
  | 'universe'
  | 'workbench'
  | 'dashboard'
  | 'reports'
  | 'settings'
  | 'unknown'

function inferEntityType(href: string): EntityType {
  if (href.startsWith('/claims'))          return 'claim'
  if (href.startsWith('/samples'))         return 'sample'
  if (href.startsWith('/rfis'))            return 'rfi'
  if (href.includes('tab=rebuttals'))      return 'rebuttal'
  if (href.includes('tab=disputes'))       return 'dispute'
  if (href.startsWith('/disagreements'))   return 'da_worksheet'
  if (href.startsWith('/universes'))       return 'universe'
  if (href.startsWith('/workbench'))       return 'workbench'
  if (href === '/')                        return 'dashboard'
  if (href.startsWith('/reports'))         return 'reports'
  if (href.startsWith('/settings'))        return 'settings'
  return 'unknown'
}

function getEntityIcon(type: EntityType): React.ElementType {
  switch (type) {
    case 'claim':        return Icons.fileText
    case 'sample':       return Icons.layers
    case 'rfi':          return Icons.alertTriangle
    case 'da_worksheet': return Icons.pencil
    case 'rebuttal':     return Icons.repeat
    case 'dispute':      return Icons.shield
    case 'universe':     return Icons.inbox
    case 'workbench':    return Icons.workbench
    case 'dashboard':    return Icons.dashboard
    case 'reports':      return Icons.barChart
    case 'settings':     return Icons.settings
    default:             return Icons.externalLink
  }
}

function getTypeLabel(type: EntityType): string {
  if (type === 'unknown') return 'Page'
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface RedirectionBlockProps {
  /** Internal frontend path (e.g. /claims/uuid). */
  href: string
  /** Display label extracted from the markdown link text. */
  label: string
}

export function RedirectionBlock({ href, label }: RedirectionBlockProps) {
  const [opened, setOpened] = React.useState(false)

  const type = inferEntityType(href)
  const EntityIcon = getEntityIcon(type)
  const typeLabel = getTypeLabel(type)

  const handleOpen = () => {
    // href is documented as an internal path (e.g. /claims/uuid); never treat
    // it as an already-qualified external URL, even if it looks like one —
    // it's AI-generated content reaching a window.open sink.
    openInNewTab(href)
    setOpened(true)
  }

  const cardId = `redirect-block-${href.replace(/\W/g, '-')}`

  return (
    <button
      type="button"
      onClick={handleOpen}
      id={cardId}
      aria-label={`Open ${label} in new tab`}
      className={cn(
        // Row layout — matches BriefingDownloadCard / NavigationCard exactly
        'flex items-center gap-3 px-4 py-3 mt-2 rounded-xl w-full text-left',
        'bg-gradient-to-r from-brand-cornflower/10 via-brand-purple/5 to-transparent',
        'border border-brand-cornflower/25',
        'hover:from-brand-cornflower/15 hover:via-brand-purple/10',
        'hover:border-brand-cornflower/40',
        'transition-all duration-200 cursor-pointer',
        'group',
        opened && 'border-emerald-300/40',
      )}
    >
      {/* Icon bubble */}
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
        'bg-gradient-to-br from-brand-navy to-brand-purple',
        'shadow-sm group-hover:shadow-md transition-shadow',
      )}>
        {opened ? (
          <Icons.checkCircle className="h-4 w-4 text-white" strokeWidth={1.5} />
        ) : (
          <EntityIcon className="h-4 w-4 text-white" strokeWidth={1.5} />
        )}
      </div>

      {/* Label + subtitle */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {typeLabel} — {label}
        </div>
        <div className={cn(
          'text-xs',
          opened ? 'text-emerald-600' : 'text-muted-foreground',
        )}>
          {opened ? 'Opened in new tab ✓' : 'Click to navigate — opens in a new tab'}
        </div>
      </div>

      {/* External link arrow */}
      <Icons.externalLink
        className="h-4 w-4 text-brand-cornflower/60 group-hover:text-brand-cornflower transition-colors shrink-0"
        strokeWidth={1.5}
      />
    </button>
  )
}
